/**
 * AdminFloatingMenu - Floating admin navigation button
 * Shows only for admin users, positioned above the main floating menu
 * Includes live counters for key admin sections
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
    LayoutDashboard, Palette, BarChart3, ShoppingBag, Package,
    ShieldCheck, Users, AlertTriangle, Flag, Activity,
    MessageCircle, Eye, FolderTree, Wallet, Megaphone, X,
    Menu
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

const AdminFloatingMenu = () => {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isOpen, setIsOpen] = useState(false);
    const [counts, setCounts] = useState({});
    const [loadingCounts, setLoadingCounts] = useState(false);
    const menuRef = useRef(null);
    const hoverTimeoutRef = useRef(null);
    const clickProcessingRef = useRef(false);

    // Sidebar items with visitors moved after users
    const sidebarItems = [
        { value: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { value: 'aspect', icon: Palette, label: 'Generale' },
        { value: 'reports', icon: BarChart3, label: 'Rapoarte' },
        { value: 'listings', icon: ShoppingBag, label: 'Listări', countKey: 'listings' },
        { value: 'orders', icon: Package, label: 'Vândute', countKey: 'orders' },
        { value: 'moderation', icon: ShieldCheck, label: 'Moderare', countKey: 'moderation' },
        { value: 'users', icon: Users, label: 'Utilizatori', countKey: 'users' },
        { value: 'visitors', icon: Eye, label: 'Vizitatori', countKey: 'visitors' },
        { value: 'disputes', icon: AlertTriangle, label: 'Dispute', countKey: 'disputes' },
        { value: 'user-reports', icon: Flag, label: 'Raportări', countKey: 'reports' },
        { value: 'logs', icon: Activity, label: 'Jurnal' },
        { value: 'conversations', icon: MessageCircle, label: 'Conversații' },
        { value: 'categories', icon: FolderTree, label: 'Categorii' },
        { value: 'escrow', icon: Wallet, label: 'Escrow' },
        { value: 'announcements', icon: Megaphone, label: 'Anunțuri' },
    ];

    // Fetch counts when menu opens
    const fetchCounts = useCallback(async () => {
        if (loadingCounts) return;
        setLoadingCounts(true);
        try {
            const API_URL = process.env.REACT_APP_BACKEND_URL || '';
            const token = localStorage.getItem('token');
            
            // Fetch dashboard stats for counts
            const response = await fetch(`${API_URL}/api/admin/dashboard`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                const totals = data.totals || {};
                
                setCounts({
                    // Listări deschise = active auctions + active sales
                    listings: (totals.active_auctions || 0) + (totals.active_sales || 0),
                    // Vândute = sold + cancelled/expired listings + cancelled_orders_count (care include toate anulările)
                    orders: (totals.sold_auctions || 0) + (totals.sold_sales || 0) + 
                            (totals.cancelled_orders_count || 0),
                    moderation: totals.pending_reports || 0,
                    users: totals.total_users || 0,
                    visitors: totals.today_unique_visitors || 0,
                    disputes: totals.pending_disputes || 0,
                    reports: totals.user_reports || 0
                });
            }
        } catch (error) {
            console.error('Error fetching admin counts:', error);
        } finally {
            setLoadingCounts(false);
        }
    }, [loadingCounts]);

    // Fetch counts when menu opens
    useEffect(() => {
        if (isOpen) {
            fetchCounts();
        }
    }, [isOpen, fetchCounts]);

    const handleNavigate = useCallback((tab) => {
        if (location.pathname === '/admin') {
            setSearchParams({ tab });
        } else {
            navigate(`/admin?tab=${tab}`);
        }
        setIsOpen(false);
    }, [navigate, location.pathname, setSearchParams]);

    const isTouchDevice = useCallback(() => {
        return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }, []);

    const handleMouseEnter = useCallback(() => {
        if (isTouchDevice()) return;
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        setIsOpen(true);
    }, [isTouchDevice]);

    const handleMouseLeave = useCallback(() => {
        if (isTouchDevice()) return;
        hoverTimeoutRef.current = setTimeout(() => {
            setIsOpen(false);
        }, 150);
    }, [isTouchDevice]);

    const handleButtonClick = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (clickProcessingRef.current) return;
        clickProcessingRef.current = true;
        
        setIsOpen(currentOpen => {
            const newState = !currentOpen;
            // Dispatch event to close other menus when this one opens
            if (newState) {
                window.dispatchEvent(new CustomEvent('adminFloatingMenuOpened'));
            }
            return newState;
        });
        
        setTimeout(() => {
            clickProcessingRef.current = false;
        }, 300);
    }, []);

    // Listen for other menus opening and close this one
    useEffect(() => {
        const handleOtherMenuOpened = () => {
            setIsOpen(false);
        };
        window.addEventListener('floatingMenuOpened', handleOtherMenuOpened);
        window.addEventListener('avatarMenuOpened', handleOtherMenuOpened);
        return () => {
            window.removeEventListener('floatingMenuOpened', handleOtherMenuOpened);
            window.removeEventListener('avatarMenuOpened', handleOtherMenuOpened);
        };
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        
        let mounted = true;
        
        const handleClickOutside = (e) => {
            if (!mounted) return;
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };

        const timeoutId = setTimeout(() => {
            if (mounted) {
                document.addEventListener('touchstart', handleClickOutside, { passive: true });
                document.addEventListener('mousedown', handleClickOutside);
            }
        }, 300);
        
        return () => {
            mounted = false;
            clearTimeout(timeoutId);
            document.removeEventListener('touchstart', handleClickOutside);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
        };
    }, []);

    // Check if mobile (using window width) - must be before conditional returns
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isAdmin = isAuthenticated && user?.role === 'admin';
    const isMessagesPage = location.pathname.startsWith('/messages');

    // Hide if not admin
    if (!isAdmin) {
        return null;
    }
    
    // Hide completely on mobile when on messages page
    if (isMessagesPage && isMobile) {
        return null;
    }

    return (
        <div 
            ref={menuRef}
            className={cn(
                "fixed right-6 z-[9998]",
                isMessagesPage ? "bottom-[144px]" : "bottom-[88px]"
            )}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            data-testid="admin-floating-menu"
            data-menu-open={isOpen ? "true" : "false"}
        >
            {/* Menu Panel */}
            <div className={cn(
                "absolute bottom-16 right-0 w-60 rounded-2xl shadow-2xl border border-gray-200 overflow-hidden transition-all duration-300 origin-bottom-right bg-white/95 backdrop-blur-sm",
                isOpen 
                    ? "opacity-100 scale-100 translate-y-0" 
                    : "opacity-0 scale-95 translate-y-2 pointer-events-none"
            )}>
                {/* Header */}
                <div className="px-4 py-3 border-b bg-gradient-to-r from-red-50 to-orange-50">
                    <div className="flex items-center justify-between">
                        <Badge className="bg-red-100 text-red-800 text-xs">
                            Admin Panel
                        </Badge>
                        <button 
                            onClick={() => setIsOpen(false)}
                            className="p-1 hover:bg-red-100 rounded-full transition-colors"
                        >
                            <X className="w-4 h-4 text-red-600" />
                        </button>
                    </div>
                </div>

                {/* Navigation Items */}
                <nav className="p-2 max-h-[60vh] overflow-y-auto">
                    <div className="space-y-0.5">
                        {sidebarItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = location.pathname === '/admin' && 
                                new URLSearchParams(location.search).get('tab') === item.value;
                            const count = item.countKey ? counts[item.countKey] : null;
                            
                            return (
                                <button
                                    key={item.value}
                                    onClick={() => handleNavigate(item.value)}
                                    data-testid={`admin-float-nav-${item.value}`}
                                    className={cn(
                                        "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                                        isActive 
                                            ? "bg-red-100 text-red-700" 
                                            : "text-gray-700 hover:bg-red-50 hover:text-red-700"
                                    )}
                                >
                                    <Icon className="w-4 h-4 shrink-0" />
                                    <span className="flex-1 text-left">{item.label}</span>
                                    {count !== null && count !== undefined && (
                                        <span className={cn(
                                            "px-2 py-0.5 text-xs font-semibold rounded-full",
                                            count > 0 
                                                ? "bg-red-100 text-red-700" 
                                                : "bg-gray-100 text-gray-500"
                                        )}>
                                            {count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </nav>
            </div>

            {/* Floating Button */}
            <button
                onClick={handleButtonClick}
                className={cn(
                    "h-14 w-14 rounded-full transition-all duration-300 flex items-center justify-center backdrop-blur-sm",
                    isOpen 
                        ? "bg-red-600 text-white shadow-lg ring-2 ring-red-600 ring-offset-2" 
                        : "bg-white/20 border-2 border-red-600 shadow-lg hover:shadow-xl"
                )}
                data-testid="admin-floating-menu-button"
                title="Admin Panel"
            >
                {isOpen ? (
                    <X className="w-6 h-6" />
                ) : (
                    <Menu className="w-6 h-6 text-red-600" />
                )}
            </button>
        </div>
    );
};

export default AdminFloatingMenu;
